import { forEach } from 'ramda';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

const useConfetti = (settings: Record<string, unknown>[], interval = 40) => {
	const refAnimationInstance = useRef<MutableRefObject<unknown>>(null);
	const [intervalId, setIntervalId] = useState<number | null>(null);

	const defaultCanvasStyle = {
		position: 'fixed',
		pointerEvents: 'none',
		width: '100%',
		height: '100%',
		top: 0,
		left: 0,
		zIndex: 0,
	};

	const getInstance = useCallback((instance) => {
		refAnimationInstance.current = instance;
	}, []);

	useEffect(() => {
		return () => {
			clearInterval(intervalId ?? 0);
		};
	}, [intervalId]);

	const nextTickAnimation = useCallback(() => {
		forEach((setting) => {
			if (!refAnimationInstance?.current) return;
			refAnimationInstance.current(setting);
		}, settings);
	}, [settings]);

	const startAnimation = useCallback(() => {
		if (intervalId) return;
		const confettiInterval = setInterval(nextTickAnimation, interval);
		setIntervalId(confettiInterval);
	}, [nextTickAnimation, intervalId, interval]);

	const pauseAnimation = useCallback(() => {
		clearInterval(intervalId ?? 0);
		setIntervalId(null);
	}, [intervalId]);

	const stopAnimation = useCallback(() => {
		clearInterval(intervalId ?? 0);
		setIntervalId(null);
		refAnimationInstance?.current && refAnimationInstance?.current.reset();
	}, [intervalId]);

	return { getInstance, startAnimation, stopAnimation, pauseAnimation, defaultCanvasStyle };
};

export default useConfetti;
